import { RollbackOutlined, SaveOutlined } from "@ant-design/icons";
import {
  Breadcrumb,
  Button,
  Card,
  Checkbox,
  Col,
  Input,
  message,
  Modal,
  Row,
  Space,
  Tabs,
  Typography,
} from "antd";
import { RootState } from "app.store";
import { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Link, useHistory, useParams } from "react-router-dom";
import { CustomTheme } from "shared/components/custom-theme/custom-theme";
import { LanguageSelector } from "shared/components/language-selector";
import { Seo } from "shared/components/seo/seo";
import { UploadFileLocal } from "shared/components/upload-file/upload-file";
import {
  ADD_PRODUCT_CATEGORY_ROUTE,
  PRODUCT_CATEGORY_ROUTE,
} from "shared/constants/routes";
import useRefresh from "shared/hooks/use-refresh";
import { SeoModel } from "shared/models/seo-model";
import { cleanUnicode } from "shared/plugins/string-helper";
import { setLoading } from "states/global-value/global-value.slice";
import { fetchModifierGroupList } from "states/modifier-group/modifier-group.slice";
import {
  fetchProductAttributeGroupList,
  fetchProductAttributeList,
  fetchProductAttributeValueList,
} from "states/product-attribute/product-attribute.slice";
import {
  addProductCategoryApi,
  getProductCategoryByIdApi,
  updateProductCategoryApi,
} from "states/product-category/product-category.api";
import {
  CreateProductCategoryModel,
  ProductCategoryModel,
  ProductFilterSetting,
  UpdatedProductCategoryModel,
  UpdateProductCategoryModifierGroupModel,
  UpdateProductCategoryModifierModel,
} from "states/product-category/product-category.models";
import { ProductCategoryAttributeTab } from "./components/product-category-attribute-tab";
import { ProductCategoryFilterSetting } from "./components/product-category-filter-setting";

interface RouteParams {
  id: string;
}
interface IAddUpdateProductCategoryProps {}

export function AddUpdateProductCategory(
  props: IAddUpdateProductCategoryProps
) {
  const clientLanguages = useSelector(
    (state: RootState) => state.localization.clientLanguages,
    shallowEqual
  );
  const [currentLanguage, setCurrentLanguage] = useState<string>();
  const refresh = useRefresh();
  const dispatch = useDispatch();
  const { id } = useParams<RouteParams>();
  const [model, setModel] = useState<ProductCategoryModel>({
    seoFields: {},
    filterSetting: {},
    active: true,
  } as ProductCategoryModel);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const currentBranch = useSelector(
    (state: RootState) => state.branch.currentBranch,
    shallowEqual
  );
  const separateProduct = useSelector(
    (state: RootState) => state.globalValue.separateProductEachBranch,
    shallowEqual
  );

  const onSaved = async () => {
    if (separateProduct && (!currentBranch.id || currentBranch.id <= 0)) {
      message.error("Please select location");
      return;
    }
    dispatch(setLoading(true));
    try {
      if (isUpdateMode) {
        const payload: UpdatedProductCategoryModel = {
          id: model.id,
          name: model.name,
          slug: model.slug,
          sort: model.sort,
          avatar: model.avatar,
          seoFields: model.seoFields,
          customizeTheme: model.customizeTheme ?? {},
          active: model?.active,
          filterSetting: model?.filterSetting ?? {},
          productCategoryModifierGroups:
            model?.productCategoryModifierGroups?.map((m) => {
              return {
                modifierGroupId: m.modifierGroupId,
                sort: m.sort,
                selectType: m.selectType,
                isRequired: m.isRequired,
                productCategoryModifiers: m.productCategoryModifiers?.map(
                  (p) => {
                    return {
                      productId: p.id,
                      sort: p.sort,
                    } as UpdateProductCategoryModifierModel;
                  }
                ),
              } as UpdateProductCategoryModifierGroupModel;
            }),
          attributeGroups: model.attributeGroups,
        };
        await updateProductCategoryApi(payload, currentLanguage);
      } else {
        const payload: CreateProductCategoryModel = {
          name: model.name,
          slug: model.slug,
          sort: model.sort,
          avatar: model.avatar,
          seoFields: model.seoFields,
          customizeTheme: model.customizeTheme ?? {},
          filterSetting: model?.filterSetting ?? {},
          active: model?.active,
          productCategoryModifierGroups:
            model?.productCategoryModifierGroups?.map((m) => {
              return {
                modifierGroupId: m.modifierGroupId,
                sort: m.sort,
                selectType: m.selectType,
                isRequired: m.isRequired,
                productCategoryModifiers: m.productCategoryModifiers?.map(
                  (p) => {
                    return {
                      productId: p.id,
                      sort: p.sort,
                    } as UpdateProductCategoryModifierModel;
                  }
                ),
              } as UpdateProductCategoryModifierGroupModel;
            }),
          attributeGroups: model.attributeGroups,
          branchId: separateProduct ? currentBranch.id : undefined,
        };
        await addProductCategoryApi(payload, currentLanguage);
      }
      Modal.success({
        title: "Thông báo",
        content: "Cập nhật thành công",
        afterClose: () => {
          refresh();
        },
      });
    } catch (error: any) {
      message.error(error?.response?.data?.error?.message ?? "Lỗi hệ thống");
    }
    dispatch(setLoading(false));
  };

  useEffect(() => {
    if (clientLanguages.length > 0 && !currentLanguage) {
      setCurrentLanguage(clientLanguages.find((m) => m.isDefault)?.langCode);
    }
  }, [clientLanguages]);

  useEffect(() => {
    if (document.location.href.indexOf(ADD_PRODUCT_CATEGORY_ROUTE) > -1) {
      setIsUpdateMode(false);
    } else {
      setIsUpdateMode(true);
      getProductCategoryByIdApi(Number(id)).then((res) => {
        setModel(res.data);
      });
    }
    dispatch(fetchProductAttributeGroupList());
    dispatch(fetchProductAttributeList());
    dispatch(fetchProductAttributeValueList());
    dispatch(fetchModifierGroupList());
  }, []);

  return (
    <div className="add-update-product-category">
      <Row gutter={[16, 16]}>
        <Col flex="auto">
          <Breadcrumb className="mb-4">
            <Breadcrumb.Item>
              <Link to={PRODUCT_CATEGORY_ROUTE}>Danh mục sản phẩm</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              {isUpdateMode ? "Sửa" : "Thêm"} danh mục
            </Breadcrumb.Item>
          </Breadcrumb>
        </Col>
        <Col flex="auto" className="d-flex justify-content-end">
          <Space>
            {isUpdateMode && (
              <LanguageSelector
                onChange={async (value) => {
                  const res = await getProductCategoryByIdApi(model.id, value);
                  if (!res.data.name) {
                    Modal.confirm({
                      title: "Thông báo",
                      content:
                        "Nội dung chưa được khởi tạo cho ngôn ngữ này, bạn có muốn khởi tạo?",
                      onOk: () => {
                        setCurrentLanguage(value);
                      },
                    });
                    return;
                  } else {
                    setCurrentLanguage(value);
                    setModel(res.data);
                  }
                }}
                selectedCode={currentLanguage}
              />
            )}
            <Link to={PRODUCT_CATEGORY_ROUTE} className="mr-2">
              <Button type="default" icon={<RollbackOutlined />}>
                {"Quay lại"}
              </Button>
            </Link>
            <Button onClick={onSaved} type="primary" icon={<SaveOutlined />}>
              {"Lưu"}
            </Button>
          </Space>
        </Col>
      </Row>
      <Tabs type="card">
        <Tabs.TabPane tab={<span>Thông tin</span>} key="1">
          <Row gutter={[16, 16]}>
            <Col span="24">
              <Card title="Thông tin danh mục">
                <Input.Group className="mb-4">
                  <Typography.Text>Hình ảnh: </Typography.Text>
                  <UploadFileLocal
                    album={model.avatar ? [model.avatar] : []}
                    maxFileCount={1}
                    multiple={false}
                    onChange={(newFileList: string[]) => {
                      setModel({ ...model, avatar: newFileList[0] });
                    }}
                  />
                </Input.Group>
                <Input.Group className="mb-4">
                  <Typography.Text>Tên: </Typography.Text>
                  <Input
                    value={model.name}
                    onChange={(e) => {
                      setModel({
                        ...model,
                        name: e.target.value,
                        seoFields: isUpdateMode
                          ? model.seoFields
                          : ({
                              ...model.seoFields,
                              metaTitle: e.target.value,
                            } as SeoModel),
                        slug: cleanUnicode(e.target.value),
                      });
                    }}
                    placeholder="Name"
                  ></Input>
                </Input.Group>
                <Input.Group className="mb-4">
                  <Typography.Text>Thứ tự: </Typography.Text>
                  <Input
                    value={model.sort ?? 0}
                    onChange={(e) => {
                      setModel({
                        ...model,
                        sort: Number(e.target.value),
                      });
                    }}
                  ></Input>
                </Input.Group>
                <Input.Group className="mb-4">
                  <Typography.Text>Active: </Typography.Text>
                  <Checkbox
                    value={model.active ?? false}
                    checked={model.active ?? false}
                    onChange={(e) => {
                      setModel({
                        ...model,
                        active: e.target.checked,
                      });
                    }}
                  ></Checkbox>
                </Input.Group>
              </Card>
            </Col>
          </Row>
        </Tabs.TabPane>
        <Tabs.TabPane tab="Theme" key="giao-dien">
          <CustomTheme
            onChange={(changedModel) => {
              setModel({ ...model, customizeTheme: changedModel });
            }}
            model={model.customizeTheme ?? {}}
          />
        </Tabs.TabPane>
        <Tabs.TabPane tab={<span>Attribute</span>} key="2">
          <ProductCategoryAttributeTab
            attributeGroups={model.attributeGroups ?? []}
            onUpdated={(attributeGroups) => {
              setModel({ ...model, attributeGroups });
            }}
          />
        </Tabs.TabPane>
        <Tabs.TabPane tab="Filter" key="filter">
          <ProductCategoryFilterSetting
            onChange={(changedModel) => {
              let filterSettingTemp = {
                attributeFilters: changedModel.attributeFilters?.map((att) => {
                  return {
                    productAttributeId: att.productAttributeId,
                    logicalTerm: att.logicalTerm,
                    productAttributeValueIds: att.values?.map(
                      (attValue) => attValue.valueId
                    ),
                  };
                }),
                priceFilters: changedModel.priceFilters,
              } as ProductFilterSetting;
              setModel({ ...model, filterSetting: filterSettingTemp });
            }}
            isUpdateMode={isUpdateMode}
            model={
              model?.filterSetting ??
              ({
                priceFilters: [],
                attributeFilters: [],
              } as ProductFilterSetting)
            }
          />
        </Tabs.TabPane>
        <Tabs.TabPane tab="SEO" key="seo">
          <Seo
            model={model.seoFields ?? ({} as SeoModel)}
            slug={model.slug}
            onChange={(seoFields, slug) => {
              setModel({ ...model, slug, seoFields });
            }}
          />
        </Tabs.TabPane>
        {/* <Tabs.TabPane tab="Modifier" key="modifier">
          <ProductCategoryModifierGroupComponent
            model={model.productCategoryModifierGroups ?? []}
            onChange={(modifiers) => {
              setModel({ ...model, productCategoryModifierGroups: modifiers });
            }}
          />
        </Tabs.TabPane> */}
      </Tabs>
    </div>
  );
}
